import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { Button, Form, Modal, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import Loaders from "components/Common/Loaders";
import {
  fetchCreateAuditBatchDateRange,
  submitCreateAuditBatch,
} from "model/CallAuditModel";

const initialDateRangeState = {
  auditDateFrom: "",
  auditDateTo: "",
  displayName: "",
};

const getLoggedInCompanyDisplayName = () => {
  try {
    const raw = localStorage.getItem("userData");
    if (!raw) return "";
    const user = JSON.parse(raw);
    const nested = user?.user && typeof user.user === "object" ? user.user : null;
    return String(
      user?.companyName ??
        user?.displayName ??
        user?.name ??
        nested?.companyName ??
        nested?.displayName ??
        nested?.name ??
        ""
    ).trim();
  } catch (_error) {
    return "";
  }
};

const formatAuditDateForDisplay = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MM-DD-YYYY") : String(value);
};

const CreateAuditModal = ({
  createAuditModalOpen,
  setCreateAuditModalOpen,
  onSuccess,
}) => {
  const [dateRange, setDateRange] = useState(initialDateRangeState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const closeModal = () => {
    setCreateAuditModalOpen(false);
  };

  const loadDateRange = async () => {
    setLoading(true);
    try {
      const response = await fetchCreateAuditBatchDateRange({});
      const data = response?.data ?? response ?? {};
      setDateRange({
        auditDateFrom: data?.auditDateFrom ?? "",
        auditDateTo: data?.auditDateTo ?? "",
        displayName: String(
          data?.name ?? data?.displayName ?? data?.companyName ?? ""
        ).trim(),
      });
    } catch (error) {
      toast.error(error?.message || "Unable to load audit date range");
      setDateRange(initialDateRangeState);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (createAuditModalOpen) {
      loadDateRange();
    }
  }, [createAuditModalOpen]);

  const handleProceed = async (event) => {
    event.preventDefault();

    if (!dateRange?.auditDateFrom || !dateRange?.auditDateTo) {
      toast.error("Audit date range is not available");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        auditDateFrom: dateRange.auditDateFrom,
        auditDateTo: dateRange.auditDateTo,
      };
      const response = await submitCreateAuditBatch(payload);
      if (response?.success !== false && (response?.success || response?.isSuccess)) {
        toast.success(response?.message || "Audit batch created successfully");
        closeModal();
        onSuccess?.();
      } else {
        toast.error(response?.message || "Unable to create audit batch");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const companyLabel =
    dateRange?.displayName || getLoggedInCompanyDisplayName();
  const modalTitleSuffix = companyLabel
    ? ` - ${String(companyLabel).toUpperCase()}`
    : "";

  return (
    <Modal
      isOpen={createAuditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
      zIndex={5000}
    >
      <ModalBody
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0 text-uppercase">
            {`Create Audit${modalTitleSuffix}`}
          </h5>
          <Button close onClick={closeModal} />
        </div>
        <hr />

        {loading ? (
          <Loaders />
        ) : (
          <Form onSubmit={handleProceed}>
            <div className="input-box d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold">Audit From Date</span>
              <span>{formatAuditDateForDisplay(dateRange.auditDateFrom)}</span>
            </div>

            <div className="input-box d-flex align-items-center justify-content-between mb-3">
              <span className="fw-semibold">Audit To Date</span>
              <span>{formatAuditDateForDisplay(dateRange.auditDateTo)}</span>
            </div>

            <div className="text-end mt-4">
              <Button color="primary" type="submit" disabled={submitting}>
                {/* <i className="fa fa-save me-1" aria-hidden="true" /> */}
                Proceed
              </Button>
            </div>
          </Form>
        )}
      </ModalBody>
    </Modal>
  );
};

CreateAuditModal.propTypes = {
  createAuditModalOpen: PropTypes.bool,
  setCreateAuditModalOpen: PropTypes.func,
  onSuccess: PropTypes.func,
};

export default CreateAuditModal;
